/**
 * Routine Handlers — pushes inline RoutineConfirmationBlock into task messages
 * when a routine is created from within a task conversation.
 */

import type { Socket } from "socket.io-client";
import { EVENTS, type RoutineConfirmationBlock } from "@bash/shared";
import { useTaskStore } from "../taskStore";

interface RoutineCreatedInTaskPayload {
  taskId: string;
  routineId: string;
  name: string;
  triggerType: "schedule" | "webhook" | "app_event";
  triggerLabel: string;
  prompt: string;
  agentSlug: string | null;
  enabled: boolean;
}

export function registerRoutineHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;

  const handleRoutineCreatedInTask = (data: RoutineCreatedInTaskPayload) => {
    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(data.taskId);
      if (!task) return;

      const block: RoutineConfirmationBlock = {
        type: "routine_confirmation",
        routineId: data.routineId,
        name: data.name,
        triggerType: data.triggerType,
        triggerLabel: data.triggerLabel,
        prompt: data.prompt,
        agentSlug: data.agentSlug,
        enabled: data.enabled,
      };

      if (task.streamingMessage) {
        // Agent is actively streaming — append to current message
        task.streamingMessage.content.push(block);
      } else {
        // Agent turn already ended — add directly to committed messages
        const routineClientId = `msg-routine-${data.routineId}`;
        task.messages = [
          ...task.messages,
          {
            id: routineClientId,
            role: "assistant" as const,
            content: [block],
            timestamp: new Date(),
            agentSlug: task.agentSlug || "bash",
            identity: { kind: "optimistic", clientId: routineClientId },
          },
        ];
      }
    });
  };

  socket.on(EVENTS.ROUTINE_CREATED_IN_TASK, handleRoutineCreatedInTask);

  return () => {
    socket.off(EVENTS.ROUTINE_CREATED_IN_TASK, handleRoutineCreatedInTask);
  };
}
